import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { ocrQaApi } from './ocr-qa.api';
import type { OcrQaListParams, OcrQaTier } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const ocrQaKeys = {
  all: ['ocr-qa'] as const,
  byTier: (tier: OcrQaTier) => [...ocrQaKeys.all, tier] as const,
  lists: (tier: OcrQaTier) => [...ocrQaKeys.byTier(tier), 'list'] as const,
  list: (tier: OcrQaTier, filters: OcrQaListParams) =>
    [...ocrQaKeys.lists(tier), filters] as const,
  metrics: (tier: OcrQaTier) => [...ocrQaKeys.byTier(tier), 'metrics'] as const,
  details: (tier: OcrQaTier) => [...ocrQaKeys.byTier(tier), 'detail'] as const,
  detail: (tier: OcrQaTier, resultId: number) =>
    [...ocrQaKeys.details(tier), resultId] as const,
  containerResults: (tier: OcrQaTier, containerId: number) =>
    [...ocrQaKeys.byTier(tier), 'container', containerId] as const
};

export const ocrQaQueries = {
  list: (tier: OcrQaTier, filters: OcrQaListParams = {}) =>
    queryOptions({
      queryKey: ocrQaKeys.list(tier, filters),
      queryFn: () => ocrQaApi.getList(tier, filters),
      placeholderData: keepPreviousData,
      // A result enters this queue when an OCR worker finishes it. The tier's
      // own mutations only invalidate what the reviewer just acted on, so
      // nothing marks the queue stale when new work arrives.
      staleTime: STALE_TIME.live
    }),

  metrics: (tier: OcrQaTier) =>
    queryOptions({
      queryKey: ocrQaKeys.metrics(tier),
      queryFn: () => ocrQaApi.getMetrics(tier),
      staleTime: STALE_TIME.metrics
    }),

  detail: (tier: OcrQaTier, resultId: number) =>
    queryOptions({
      queryKey: ocrQaKeys.detail(tier, resultId),
      queryFn: () => ocrQaApi.getById(tier, resultId)
    }),

  containerResults: (tier: OcrQaTier, containerId: number) =>
    queryOptions({
      queryKey: ocrQaKeys.containerResults(tier, containerId),
      queryFn: () => ocrQaApi.getContainerResults(tier, containerId)
    })
};
